<template>
  <div class="flex relative px-6 py-5 gap-4">
    <div class="flex-1">
      <template v-if="review.includedReviewItems.value.length">
        <div
          class="inline-grid w-full border-t border-r border-neutral-border-default rounded-lg"
          style="grid-template-columns: 420px 1fr"
        >
          <div class="bg-neutral-background-default h-12.5 rounded-tl-lg sticky top-13 left-0 z-51">
            <div
              ref="searchContainerRef"
              class="h-full text-sm pr-2 pl-4 py-3.5 justify-between border-neutral-border-default border-b border-l flex items-center font-medium rounded-tl-lg"
            >
              <Transition name="search-fade" mode="out-in">
                <input
                  v-if="isSearchOpen"
                  ref="searchInputRef"
                  v-model="searchQuery"
                  type="text"
                  class="flex-1 min-w-0 bg-transparent text-neutral-text-title outline-none placeholder:text-neutral-text-subtitle"
                  placeholder="Search extracted text..."
                  @keydown.escape="closeSearch"
                />
                <span v-else class="text-neutral-text-title">Extracted Records</span>
              </Transition>
              <button
                v-if="selectedAttributeType !== AttributeStructureType.StudyDesign"
                class="relative flex items-center gap-1 rounded-md p-1 text-neutral-text-title shrink-0 ml-2"
                v-tooltip="'Search'"
                @click="toggleSearch"
              >
                <i-material-symbols-search v-if="!isSearchOpen" class="size-4" />
                <i-material-symbols-close v-else class="size-4" />
                <span
                  v-if="searchQuery && !isSearchOpen"
                  class="absolute -top-1 -right-1 flex h-2.5 w-2.5"
                >
                  <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
                  ></span>
                  <span
                    class="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-white"
                  ></span>
                </span>
              </button>
            </div>
          </div>

          <div class="bg-neutral-background-default h-12.5 sticky top-13 left-0 z-40 rounded-r-lg">
            <div
              :title="sortedAttributes[selectedAttributeIndex]?.question"
              class="h-full text-sm border-l border-b border-neutral-border-default text-neutral-text-title flex p-3.5 items-center font-medium justify-between"
            >
              <div class="truncate">
                {{
                  sortedAttributes[selectedAttributeIndex]?.label !== ""
                    ? sortedAttributes[selectedAttributeIndex]?.label
                    : "Question unspecified"
                }}
                – Synthesis
              </div>
            </div>
          </div>

          <div
            class="border-l border-b bg-white border-neutral-border-default overflow-y-auto h-[calc(100vh-140px)] rounded-bl-lg"
          >
            <template v-if="reviewItemsWithExtraction.length">
              <div
                v-for="reviewItem in reviewItemsWithExtraction"
                :key="reviewItem.id"
                class="h-fit"
              >
                <ReviewItemDataExtractionCard
                  :review-item="reviewItem"
                  :item-url="reviewItem.metadata.url"
                  :extracted-text="reviewItem.extractedText"
                  :attribute-value="reviewItem.attributeValue"
                  :attribute-type="reviewItem.attributeType"
                />
              </div>
            </template>

            <div v-else class="p-4 text-center text-neutral-text-subtitle">
              No articles match your filters
            </div>
          </div>

          <div class="h-[calc(100vh-140px)]">
            <AttributeSynthesis
              :key="sortedAttributes[selectedAttributeIndex]?.id"
              :attribute-structure="sortedAttributes[selectedAttributeIndex]"
            />
          </div>
        </div>
      </template>

      <div v-else-if="review.entity.value?.studies?.length" class="h-full">
        No articles have been included
      </div>
      <div v-else>This review is empty</div>
    </div>

    <div
      v-if="review.includedReviewItems.value.length"
      v-discrete-scrollbar
      class="w-[260px] sticky top-13 right-0 h-[calc(100vh-80px)] overflow-y-auto flex flex-col items-start gap-3 z-1"
    >
      <button
        v-for="(attribute, index) in sortedAttributes"
        :key="attribute.id"
        class="px-3.5 py-2 w-full text-left rounded-lg transition-colors flex justify-between hover:bg-white hover:shadow hover:shadow-black/10"
        :class="{
          'bg-white shadow shadow-black/10 text-neutral-text-title':
            index === selectedAttributeIndex,
          'text-gray-400 hover:bg-white': index !== selectedAttributeIndex,
        }"
        @click="selectedAttributeIndex = index"
      >
        <span
          v-if="attribute.label !== ''"
          :title="attribute.label"
          class="text-base font-medium truncate"
        >
          {{ attribute.label }}
        </span>
        <router-link
          v-else
          v-tooltip.top="'Click to add the missing label in the plan'"
          :to="`/reviews/${review.entity.value.id}/plan/objective`"
          class="text-base font-medium truncate"
          @click.stop
        >
          Question unspecified
        </router-link>
        <i-material-symbols-check-circle
          v-if="attributeSynthesisFinalizationState[index]"
          class="size-6 text-green-500 shrink-0"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../use-review";
import AttributeSynthesis from "./AttributeSynthesis.vue";
import ReviewItemDataExtractionCard from "./ReviewItemDataExtractionCard.vue";
import { AttributeStructureType } from "@core/domain/types/attributeStructureType.type";
import type {
  MedicalAlternativesValue,
  StudyDesignValue,
} from "@core/domain/types/attributeValue.type";

const review = injectStrict(ReviewKey);

const searchQuery = ref("");
const isSearchOpen = ref(false);
const searchInputRef = ref<HTMLInputElement | null>(null);
const searchContainerRef = ref<HTMLDivElement | null>(null);

function toggleSearch() {
  if (isSearchOpen.value) {
    closeSearch();
  } else {
    openSearch();
  }
}

async function openSearch() {
  isSearchOpen.value = true;
  await nextTick();
  searchInputRef.value?.focus();
}

function closeSearch() {
  isSearchOpen.value = false;
}

onClickOutside(searchContainerRef, () => {
  if (isSearchOpen.value) closeSearch();
});

const sortedAttributes = computed(() =>
  review.entity.value.plan!.synthesisPlan!.attributesStructure.toSorted((a, b) => {
    if (a.order === null && b.order === null) return 0;
    if (a.order === null) return -1;
    if (b.order === null) return 1;
    return a.order > b.order ? 1 : -1;
  }),
);

const attributeSynthesisFinalizationState = computed(() =>
  sortedAttributes.value.map(
    (attr) =>
      review.entity.value.plan?.synthesisPlan.attributesStructure.find((p) => p.id === attr.id)
        ?.isSynthesisFinalized === true,
  ),
);

const selectedAttributeIndex = ref(0);

const selectedAttributeId = computed(
  () => sortedAttributes.value[selectedAttributeIndex.value]?.id,
);

const selectedAttributeType = computed(
  () => sortedAttributes.value[selectedAttributeIndex.value]?.type,
);

const reviewItemsWithExtraction = computed(() => {
  const attributeId = selectedAttributeId.value;
  const attributeType = selectedAttributeType.value;
  if (!attributeId) return [];

  const finalizedItems = review.filteredIncludedReviewItems.value.filter((item) =>
    item.synthesis?.attributes?.some(
      (attr) => attr.attributeStructureId === attributeId && attr.isFinalized === true,
    ),
  );

  if (attributeType === AttributeStructureType.MedicalAlternatives) {
    const query = searchQuery.value.toLowerCase().trim();
    return finalizedItems
      .map((item) => {
        const value = item.synthesis?.attributes?.find(
          (attr) => attr.attributeStructureId === attributeId,
        )?.value as MedicalAlternativesValue | undefined;
        return {
          ...item,
          extractedText: "",
          attributeValue: value ?? null,
          attributeType,
        };
      })
      .filter((item) => {
        const value = item.attributeValue as MedicalAlternativesValue | null;
        return (value?.alternatives?.length ?? 0) > 0;
      })
      .filter((item) => {
        if (!query) return true;
        const value = item.attributeValue as MedicalAlternativesValue | null;
        return value?.alternatives?.some(
          (alt) =>
            alt.name?.toLowerCase().includes(query) ||
            alt.description?.toLowerCase().includes(query),
        );
      });
  }

  if (attributeType === AttributeStructureType.StudyDesign) {
    return finalizedItems
      .map((item) => {
        const value = item.synthesis?.attributes?.find(
          (attr) => attr.attributeStructureId === attributeId,
        )?.value as StudyDesignValue | undefined;
        return {
          ...item,
          extractedText: "",
          attributeValue: value ?? null,
          attributeType,
        };
      })
      .filter((item) => {
        const value = item.attributeValue as StudyDesignValue | null;
        return (
          (value?.populations?.length ?? 0) > 0 ||
          (value?.arms?.length ?? 0) > 0 ||
          (value?.periods?.length ?? 0) > 0
        );
      });
  }
  return finalizedItems
    .map((item) => ({
      ...item,
      extractedText: getHocuspocusText(item.id, attributeId),
      attributeValue: null,
      attributeType,
    }))
    .filter((item) => {
      const text = item.extractedText.toLowerCase().trim();
      return text && text !== "not reported";
    })
    .filter((item) => {
      if (!searchQuery.value.trim()) return true;
      return item.extractedText.toLowerCase().includes(searchQuery.value.toLowerCase().trim());
    });
});

function getHocuspocusText(studyId: number, attributeId: string): string {
  if (!attributeId) return "";

  const field = `study::${studyId}|attribute::${attributeId}`;
  const fragment = review.dataExtractionProvider.document.getXmlFragment(field);

  let text = "";

  fragment.toArray().forEach((node: any) => {
    if (node.text) {
      text += node.text;
    } else if (node.toString) {
      text += node.toString().replaceAll(/<\/?[^>]+>/g, "");
    }
  });

  return text.trim();
}
</script>

<style scoped>
.search-fade-enter-active,
.search-fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.search-fade-enter-from {
  opacity: 0;
  transform: translateX(-4px);
}

.search-fade-leave-to {
  opacity: 0;
  transform: translateX(4px);
}
</style>
